import {
	AVATARS,
	CATEGORY_LABEL,
	DIFFICULTIES,
	type Difficulty,
	GAME_CATEGORIES,
	GAME_LIST,
	ROOM_CODE_LENGTH,
} from "@playpal/core";
import { useState } from "react";
import { forgetRoom, lastPlayedLabel, lastRoom, partners, playedGames } from "../lib/history";
import { type Identity, randomName, saveIdentity } from "../lib/identity";
import type { Session } from "../lib/session";
import { CodeEntry } from "./CodeEntry";
import { Button, Card, Panel } from "./ui";

interface Props {
	identity: Identity;
	session: Session;
	onIdentityChange: (identity: Identity) => void;
	onCreate: () => void;
	onPlayComputer: (difficulty: Difficulty) => void;
	onJoin: (code: string) => void;
	onOpenAuth: () => void;
	onOpenProfile: () => void;
	busy: boolean;
	error: string | null;
}

const DIFFICULTY_COPY: Record<Difficulty, { label: string; blurb: string }> = {
	easy: { label: "Easy", blurb: "Makes mistakes" },
	medium: { label: "Medium", blurb: "Pays attention" },
	hard: { label: "Hard", blurb: "Plays properly" },
};

export function HomeScreen({
	identity,
	session,
	onIdentityChange,
	onCreate,
	onPlayComputer,
	onJoin,
	onOpenAuth,
	onOpenProfile,
	busy,
	error,
}: Props) {
	const [code, setCode] = useState("");
	// Read once: a room that ages out mid-visit should not make the button
	// vanish from under a thumb that is already on the way to it.
	const [rejoinable, setRejoinable] = useState(lastRoom);
	// Read once too, for the same reason — and so the labels don't shuffle
	// underneath a child mid-look.
	const [pals] = useState(partners);
	const played = playedGames().length;
	const [difficulty, setDifficulty] = useState<Difficulty>("medium");
	// Folded away by default: you choose an animal once, and it was taking two
	// fifths of the card every time afterwards.
	const [pickingAvatar, setPickingAvatar] = useState(false);
	const ready = code.length === ROOM_CODE_LENGTH;

	const update = (next: Identity) => {
		saveIdentity(next);
		onIdentityChange(next);
	};

	return (
		<Panel>
			<header className="mb-4 text-center">
				<h1 className="text-5xl font-black tracking-tight text-indigo-900 drop-shadow-sm sm:text-6xl dark:text-indigo-50">
					PlayPal
				</h1>
				<p className="mt-1 text-indigo-900/70 dark:text-indigo-100/80">
					{GAME_LIST.length} games. Two players. One four-letter code.
				</p>
			</header>

			<Card className="mb-4 p-5 sm:p-6">
				<div className="flex items-center gap-2 sm:gap-3">
					{/* Tapping the animal is how you change it. A picture of yourself
					    with a pencil on it is the one control everybody already knows,
					    and it buys back the whole row a "Change animal" button cost. */}
					{session.signedIn ? (
						<span className="shrink-0 text-4xl" aria-hidden="true">
							{identity.avatar}
						</span>
					) : (
						<button
							type="button"
							aria-label="Change your animal"
							aria-expanded={pickingAvatar}
							onClick={() => setPickingAvatar((open) => !open)}
							className="relative shrink-0 rounded-2xl px-1 text-4xl transition hover:scale-105"
						>
							{identity.avatar}
							<span
								aria-hidden="true"
								className="absolute -right-1 -bottom-1 rounded-full bg-white text-xs shadow-md dark:bg-indigo-900"
							>
								✏️
							</span>
						</button>
					)}
					{/* min-w-0 lets the name column shrink instead of forcing the row
					    wider than the card, which is what pushed it onto two lines. */}
					<div className="min-w-0 flex-1">
						{/* Steps down on a narrow phone so the longest name in the list
						    — Cheerful Hedgehog — still fits beside the animal. */}
						<p className="truncate text-xl font-bold sm:text-2xl">{identity.name}</p>
						{session.signedIn ? (
							<p className="truncate text-sm font-semibold opacity-70">
								Player card {session.profile?.code}
							</p>
						) : (
							<p className="truncate text-sm opacity-70">Playing as a guest</p>
						)}
					</div>
				</div>

				<div className="mt-3 flex flex-wrap items-center gap-2">
					<Button
						tone="secondary"
						size="sm"
						onClick={session.signedIn ? onOpenProfile : onOpenAuth}
					>
						{session.signedIn ? "🏆 My record" : "💾 Save my wins"}
					</Button>
					{!session.signedIn && (
						<Button
							tone="ghost"
							size="sm"
							onClick={() => update({ ...identity, name: randomName() })}
						>
							🎲 New name
						</Button>
					)}
				</div>

				{!session.signedIn && (
					<fieldset className={pickingAvatar ? "mt-3" : "hidden"}>
						<legend className="mb-2 font-semibold">Pick your animal</legend>
						<div className="flex flex-wrap gap-2">
							{AVATARS.map((avatar) => {
								const chosen = avatar === identity.avatar;
								return (
									<button
										key={avatar}
										type="button"
										aria-label={`Choose ${avatar}`}
										aria-pressed={chosen}
										onClick={() => {
											update({ ...identity, avatar });
											setPickingAvatar(false);
										}}
										className={`rounded-2xl px-3 py-2 text-3xl transition ${
											chosen
												? "scale-110 bg-indigo-600/90 shadow-lg"
												: "bg-white/60 hover:scale-105 dark:bg-white/10"
										}`}
									>
										{avatar}
									</button>
								);
							})}
						</div>
					</fieldset>
				)}
			</Card>

			{/*
			 * The way back into a game you were already in.
			 *
			 * Closing the tab, backgrounding the app until iOS kills it, or
			 * tapping Leave by accident all used to mean asking your friend to
			 * read the code out again — a room lives two hours, so it was almost
			 * always still sitting there.
			 */}
			{rejoinable && (
				<Card className="mb-4 flex flex-wrap items-center gap-3">
					<span className="text-3xl" aria-hidden="true">
						{rejoinable.friendAvatar ?? "🎲"}
					</span>
					<div className="min-w-0 flex-1">
						<p className="font-bold">
							{rejoinable.friendName ? `Back to ${rejoinable.friendName}` : "Your last game"}
						</p>
						<p className="text-sm opacity-70">Room {rejoinable.code}</p>
					</div>
					<Button size="sm" onClick={() => onJoin(rejoinable.code)} disabled={busy}>
						↩️ Rejoin
					</Button>
					<Button
						size="sm"
						tone="ghost"
						onClick={() => {
							forgetRoom();
							setRejoinable(null);
						}}
					>
						Hide
					</Button>
				</Card>
			)}

			{/* Two columns even on a narrow phone: starting a game and joining one
			    are the whole point of this screen, and stacking them pushed the
			    second below the fold. */}
			<div className="grid grid-cols-2 gap-3">
				<Card className="flex flex-col items-center justify-between gap-3 px-3 text-center">
					<h2 className="text-xl font-bold">Play a friend</h2>
					<p className="text-sm text-indigo-900/70 dark:text-indigo-100/80">
						You'll get a code to share.
					</p>
					<Button size="lg" onClick={onCreate} disabled={busy}>
						{busy ? "…" : "🎮 Start"}
					</Button>
				</Card>

				<Card className="flex flex-col items-center justify-between gap-3 px-3 text-center">
					<h2 className="text-xl font-bold">Join a friend</h2>
					<CodeEntry value={code} onChange={setCode} onComplete={onJoin} />
					<Button size="lg" onClick={() => onJoin(code)} disabled={!ready || busy}>
						👋 Join
					</Button>
				</Card>
			</div>

			{/*
			 * The friends you already play, one tap from playing them again.
			 *
			 * A room dies within hours, so by tomorrow the only way back to the
			 * same friend was the whole dance from scratch. This shortcut is
			 * honest about what it can do: there is no way to ring the friend's
			 * device, so the button opens a fresh room and lands on the share
			 * screen — the code still travels the way it always does, read out or
			 * sent along. The list lives in this browser and nowhere else.
			 */}
			{pals.length > 0 && (
				<Card className="mt-4">
					<h2 className="text-xl font-bold">Play again</h2>
					<p className="mt-1 text-sm text-indigo-900/70 dark:text-indigo-100/80">
						Start a new room and send them the code.
					</p>
					<ul className="mt-3 space-y-2">
						{pals.map((pal) => (
							<li
								key={pal.playerCode ?? `${pal.name} ${pal.avatar}`}
								className="flex items-center gap-3 rounded-2xl bg-white/60 px-4 py-3 dark:bg-white/10"
							>
								<span className="text-3xl" aria-hidden="true">
									{pal.avatar}
								</span>
								<div className="min-w-0 flex-1">
									<p className="truncate font-bold">{pal.name}</p>
									<p className="text-sm opacity-70">{lastPlayedLabel(pal.at)}</p>
								</div>
								<Button
									size="sm"
									tone="secondary"
									onClick={onCreate}
									disabled={busy}
									aria-label={`Play ${pal.name} again`}
								>
									🎮 New game
								</Button>
							</li>
						))}
					</ul>
				</Card>
			)}

			<Card className="mt-4 text-center">
				<h2 className="text-2xl font-bold">Play the computer</h2>
				<p className="mt-1 mb-4 text-indigo-900/70 dark:text-indigo-100/80">
					No friend around? Robo will play you right now.
				</p>
				{/* Three columns rather than a wrapping row: at phone width "Hard"
				    used to drop onto a line of its own, which read as a lesser option
				    rather than the third of three. */}
				<div className="mb-4 grid grid-cols-3 gap-2">
					{DIFFICULTIES.map((level) => (
						<button
							key={level}
							type="button"
							aria-pressed={difficulty === level}
							onClick={() => setDifficulty(level)}
							className={`rounded-2xl px-2 py-3 font-bold shadow-md transition ${
								difficulty === level
									? "bg-indigo-600 text-white"
									: "bg-white/70 hover:bg-white dark:bg-white/10"
							}`}
						>
							<span className="block text-lg">{DIFFICULTY_COPY[level].label}</span>
							<span className="block text-xs font-semibold opacity-70">
								{DIFFICULTY_COPY[level].blurb}
							</span>
						</button>
					))}
				</div>
				<Button size="lg" onClick={() => onPlayComputer(difficulty)} disabled={busy}>
					🤖 Play Robo
				</Button>
			</Card>

			{error && (
				<p
					role="alert"
					className="mt-6 rounded-2xl bg-rose-100 px-4 py-3 text-center text-lg font-semibold text-rose-900"
				>
					{error}
				</p>
			)}

			<section className="mt-10">
				<h2 className="mb-1 text-center text-xl font-bold text-indigo-900/80 dark:text-indigo-100/80">
					What's inside
				</h2>
				<p className="mb-4 text-center text-sm text-indigo-900/60 dark:text-indigo-100/60">
					You pick the game once you're in a room — there's a search box.
				</p>

				{/*
				 * A shelf per line rather than forty-three cards. The full list used
				 * to run down the front page, which made the two things a child is
				 * actually here to do — start a game, or join one — scroll away.
				 */}
				<ul className="mx-auto grid max-w-lg gap-2">
					{GAME_CATEGORIES.filter((name) => GAME_LIST.some((game) => game.category === name)).map(
						(name) => {
							const games = GAME_LIST.filter((game) => game.category === name);
							return (
								<li
									key={name}
									className="flex items-center gap-3 rounded-2xl bg-white/60 px-4 py-3 dark:bg-white/10"
								>
									<span className="text-2xl" aria-hidden="true">
										{CATEGORY_LABEL[name].emoji}
									</span>
									<span className="font-bold">{CATEGORY_LABEL[name].title}</span>
									<span className="ml-auto shrink-0 text-sm font-semibold text-indigo-900/60 dark:text-indigo-100/60">
										{games.length}
									</span>
								</li>
							);
						},
					)}
				</ul>

				<p className="mt-4 text-center text-lg font-bold">
					{played > 0
						? `You've tried ${played} of ${GAME_LIST.length} so far.`
						: `${GAME_LIST.length} games in all — and the computer will play any of them.`}
				</p>
			</section>

			<p className="mt-10 text-center text-sm text-indigo-900/60 dark:text-indigo-100/60">
				No ads and no chat. Names come from a list, so nobody can type anything unkind.
			</p>
			<p className="mt-2 text-center text-sm">
				<a
					href="/privacy"
					className="font-semibold text-indigo-900/70 underline dark:text-indigo-100/70"
				>
					Privacy
				</a>
				<span className="mx-2 opacity-40">·</span>
				<a
					href="/support"
					className="font-semibold text-indigo-900/70 underline dark:text-indigo-100/70"
				>
					Help
				</a>
			</p>
		</Panel>
	);
}
